import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, RefreshCw, AlertCircle, Loader2, Shield, LogOut, Settings, Eye, EyeOff, ExternalLink, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function M365IntegrationPage() {
  const { user, viewingOrg } = useContext(AuthContext);
  const isSuperAdmin = user?.role === 'super_admin';
  
  const [activeTab, setActiveTab] = useState('connection');
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [connectedAt, setConnectedAt] = useState(null);
  const [connectedAccount, setConnectedAccount] = useState(null);
  const [securityPosture, setSecurityPosture] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [oauthError, setOauthError] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [loadingPosture, setLoadingPosture] = useState(false);
  
  // Settings state
  const [config, setConfig] = useState({ client_id: '', client_secret: '', azure_tenant_id: 'common', redirect_uri: '' });
  const [showSecret, setShowSecret] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const redirectUri = `${window.location.origin}/api/auth/m365/callback`;

  useEffect(() => {
    checkM365Status();
    loadConfig();
    
    // Check for OAuth callback success/error
    const params = new URLSearchParams(window.location.search);
    
    if (params.get('connected') === 'true') {
      toast.success('Microsoft 365 connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
      // Refresh status to show new connection
      setTimeout(() => checkM365Status(), 500);
    } else if (params.get('error')) {
      const error = params.get('error');
      const errorDesc = params.get('error_description');
      const errorMessage = errorDesc ? decodeURIComponent(errorDesc.replace(/\+/g, ' ')) : error;
      setOauthError(errorMessage);
      
      if (error === 'consent_required') {
        toast.error('Admin consent is required. Please have an administrator connect Microsoft 365.');
      } else if (error === 'no_tenant') {
        toast.error('Please select an organization before connecting Microsoft 365.');
      } else {
        toast.error(`Microsoft 365 Error: ${errorMessage}`);
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [viewingOrg]);

  const loadConfig = async () => {
    try {
      const params = isSuperAdmin && viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
      const response = await axios.get(`${API}/m365/config${params}`);
      if (response.data.client_id) {
        setConfig({
          client_id: response.data.client_id || '',
          client_secret: '', // Never returned from backend
          azure_tenant_id: response.data.azure_tenant_id || 'common',
          redirect_uri: response.data.redirect_uri || redirectUri
        });
      }
      setConfigLoaded(true);
    } catch (error) {
      console.error('Failed to load M365 config:', error);
      setConfigLoaded(true);
    }
  };

  const checkM365Status = async () => {
    setLoading(true);
    try {
      const params = isSuperAdmin && viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
      const response = await axios.get(`${API}/m365/status${params}`);
      
      // Check if reconnection is needed
      if (response.data.needs_reauth) {
        setConnected(false);
        setOauthError('Your Microsoft 365 connection needs to be re-established. Please reconnect.');
        setSecurityPosture(null);
        return;
      }
      
      setConnected(response.data.connected);
      setConnectedAt(response.data.connected_at);
      setConnectedAccount({
        email: response.data.connected_email,
        displayName: response.data.connected_display_name,
        domain: response.data.domain,
        organization: response.data.organization
      });
      
      if (response.data.connected) {
        fetchSecurityPosture();
      } else {
        setSecurityPosture(null);
      }
    } catch (error) {
      console.error('Failed to check M365 status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSecurityPosture = async () => {
    setLoadingPosture(true);
    try {
      const params = isSuperAdmin && viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
      const response = await axios.get(`${API}/m365/security-posture${params}`);
      
      // Check if response indicates not connected
      if (response.data.not_connected) {
        console.log('M365 not connected:', response.data.message);
        setConnected(false);  // Update connected state
        setSecurityPosture(null);
        return;
      }
      
      // Check if response has error field (from service layer)
      if (response.data.error) {
        console.error('M365 security posture error:', response.data.error);
        setSecurityPosture({ 
          error: true, 
          message: response.data.error
        });
        return;
      }
      
      setSecurityPosture(response.data);
    } catch (error) {
      console.error('Failed to fetch security posture:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.response?.data?.error || "Unable to fetch security data";
      
      // Check if it's a "not connected" error
      if (errorMsg.includes("not connected") || error.response?.data?.not_connected) {
        setConnected(false);
        setSecurityPosture(null);
        return;
      }
      
      setSecurityPosture({ 
        error: true, 
        message: errorMsg.includes("session") 
          ? "Your Microsoft session has expired or is invalid. Please disconnect and sign in again."
          : `Error: ${errorMsg}. Please try disconnecting and signing in again.`
      });
    } finally {
      setLoadingPosture(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    setOauthError(null);
    try {
      // Pass view_tenant_id for super admin viewing a specific org
      const params = isSuperAdmin && viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
      const response = await axios.get(`${API}/auth/m365/login${params}`);
      
      if (response.data.already_connected) {
        toast.info('Already connected to Microsoft 365');
        checkM365Status();
        setConnecting(false);
        return;
      }
      
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      } else {
        toast.error('No authorization URL returned');
        setConnecting(false);
      }
    } catch (error) {
      console.error('Failed to initiate M365 login:', error);
      toast.error(error.response?.data?.detail || 'Failed to connect to Microsoft 365');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect Microsoft 365? You will need to reconnect to see security data.')) {
      return;
    }
    
    try {
      const params = isSuperAdmin && viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
      await axios.post(`${API}/m365/disconnect${params}`);
      toast.success('Microsoft 365 disconnected. You can now reconnect.');
      setConnected(false);
      setSecurityPosture(null);
      setConnectedAccount({});
      setOauthError(null);
    } catch (error) {
      console.error('Failed to disconnect M365:', error);
      toast.error(error.response?.data?.detail || 'Failed to disconnect');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await fetchSecurityPosture();
      toast.success('Security data refreshed');
    } catch (error) {
      console.error('Sync failed:', error);
      toast.error('Failed to sync security data');
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!config.client_id) {
      toast.error('Client ID is required');
      return;
    }
    
    setSavingConfig(true);
    try {
      await axios.post(`${API}/m365/config`, {
        client_id: config.client_id,
        client_secret: config.client_secret || undefined,
        azure_tenant_id: config.azure_tenant_id || 'common',
        redirect_uri: config.redirect_uri || redirectUri,
        view_tenant_id: isSuperAdmin && viewingOrg?.id ? viewingOrg.id : undefined
      });
      toast.success('Configuration saved successfully');
      setConfig(prev => ({ ...prev, client_secret: '' })); // Clear secret after save
    } catch (error) {
      console.error('Failed to save config:', error);
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight text-slate-900">
            Microsoft 365 Security
          </h1>
          <p className="text-slate-600 mt-1">
            Connect your Microsoft 365 tenant to monitor security controls
          </p>
        </div>
        {connected && (
          <Button
            onClick={handleSync}
            disabled={syncing}
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Refresh'}
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('connection')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'connection'
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            Connection
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'settings'
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            <Settings className="w-4 h-4" />
            Azure AD Settings
          </button>
        </nav>
      </div>

      {/* OAuth Error Display */}
      {oauthError && (
        <div className="bg-red-50 border border-red-200 rounded-sm p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-red-900">Connection Failed</h3>
              <p className="text-red-700 mt-1">{oauthError}</p>
              {oauthError.includes('administrator') && (
                <p className="text-sm text-red-600 mt-2">
                  An administrator in your organization needs to approve this application. 
                  Please contact your IT admin or sign in with an admin account.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Connection Tab */}
      {activeTab === 'connection' && (
        <>
          {/* Not Connected State */}
          {!connected && !oauthError && (
            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
              <div className="p-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-[#0078D4] to-[#106EBE] rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Shield className="w-10 h-10 text-white" />
                </div>
                
                <h2 className="text-2xl font-semibold text-slate-900 mb-2">
                  Connect Microsoft 365
                </h2>
                <p className="text-slate-600 mb-6 max-w-md mx-auto">
                  Connect your Microsoft 365 tenant to automatically collect security data including 
                  Secure Score, MFA status, Conditional Access, Defender alerts, and more.
                </p>
                
                <Button
                  onClick={handleConnect}
                  disabled={connecting}
                  className="bg-[#0078D4] text-white hover:bg-[#106EBE] rounded-sm px-8 py-6 text-lg"
                  data-testid="m365-connect-button"
                >
                  {connecting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 23 23" fill="currentColor">
                        <path d="M0 0h11v11H0V0zm12 0h11v11H12V0zM0 12h11v11H0V12zm12 0h11v11H12V12z"/>
                      </svg>
                      Sign in with Microsoft
                    </>
                  )}
                </Button>

                <div className="mt-6 text-sm text-slate-500">
                  <p className="mb-2">✓ Uses your Azure AD app configuration (see Settings tab)</p>
                  <p className="mb-2">✓ Admin consent requested automatically</p>
                  <p>✓ Secure OAuth 2.0 authentication</p>
                </div>
              </div>

              {/* What we collect */}
              <div className="bg-slate-50 border-t border-slate-200 p-6">
                <h3 className="font-medium text-slate-900 mb-4">Security Data We Collect</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-slate-600">
                  <div>
                    <p className="font-medium text-slate-700">Identity</p>
                    <ul className="mt-1 space-y-1">
                      <li>• MFA Status</li>
                      <li>• Conditional Access</li>
                      <li>• Risk Policies</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-slate-700">Security</p>
                    <ul className="mt-1 space-y-1">
                      <li>• Secure Score</li>
                      <li>• Security Alerts</li>
                      <li>• Defender Status</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-slate-700">Compliance</p>
                    <ul className="mt-1 space-y-1">
                      <li>• Audit Logs</li>
                      <li>• DLP Policies</li>
                      <li>• Sensitivity Labels</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-slate-700">Devices</p>
                    <ul className="mt-1 space-y-1">
                      <li>• Compliance Status</li>
                      <li>• BitLocker</li>
                      <li>• Update Policies</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Session Expired State */}
          {connected && securityPosture?.error && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-amber-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-medium text-amber-900">Session Issue</h3>
                  <p className="text-amber-700 mt-1">{securityPosture.message}</p>
                  <div className="mt-4 flex gap-3">
                    <Button
                      onClick={handleDisconnect}
                      variant="outline"
                      className="border-amber-300 text-amber-800 hover:bg-amber-100"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Disconnect
                    </Button>
                    <Button
                      onClick={handleConnect}
                      className="bg-amber-600 text-white hover:bg-amber-700"
                    >
                      Reconnect
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Loading Security Posture State */}
          {connected && loadingPosture && !securityPosture && (
            <div className="space-y-4">
              {connectedAccount && (
                <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#0078D4] rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">
                        {connectedAccount.displayName?.charAt(0) || connectedAccount.email?.charAt(0) || 'M'}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="font-medium text-slate-900">Connected to Microsoft 365</span>
                      </div>
                      <p className="text-sm text-slate-600">
                        {connectedAccount.organization || connectedAccount.domain || connectedAccount.email}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="bg-white border border-slate-200 rounded-sm p-8">
                <div className="flex flex-col items-center justify-center">
                  <Loader2 className="w-10 h-10 text-[#0078D4] animate-spin mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Loading Security Data</h3>
                  <p className="text-slate-600 text-center max-w-md">
                    Fetching your Microsoft 365 security posture and checking 21 priority controls...
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Connected State */}
          {connected && securityPosture && !securityPosture.error && (
            <>
              {/* Connected Account Banner */}
              {connectedAccount && (connectedAccount.email || connectedAccount.organization) && (
                <div className="bg-green-50 border border-green-200 rounded-sm p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#0078D4] rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold text-sm">
                          {connectedAccount.displayName?.charAt(0) || connectedAccount.email?.charAt(0) || 'M'}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="font-medium text-slate-900">Connected to Microsoft 365</span>
                        </div>
                        <p className="text-sm text-slate-600">
                          {connectedAccount.organization && <span className="font-medium">{connectedAccount.organization}</span>}
                          {connectedAccount.organization && connectedAccount.domain && <span> · </span>}
                          {connectedAccount.domain && <span>{connectedAccount.domain}</span>}
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={handleDisconnect}
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Disconnect
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Microsoft Secure Score Card */}
              {(securityPosture.secure_score !== undefined || securityPosture.max_score !== undefined) && (
                <div className="bg-gradient-to-r from-[#0078D4] to-[#106EBE] rounded-sm p-5 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-100 mb-1">Microsoft Secure Score</p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-bold">{securityPosture.secure_score || 0}</span>
                        <span className="text-xl text-blue-200">/ {securityPosture.max_score || 0}</span>
                      </div>
                      <p className="text-sm text-blue-100 mt-1">
                        {securityPosture.secure_score_percentage || 0}% of maximum score achieved
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="w-20 h-20 rounded-full border-4 border-white/30 flex items-center justify-center bg-white/10">
                        <span className="text-2xl font-bold">{securityPosture.secure_score_percentage || 0}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Control Check Overview */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white border border-border rounded-sm p-4">
                  <p className="text-sm font-medium text-slate-600 mb-1">Controls Completion</p>
                  <p className={`text-3xl font-heading font-semibold ${
                    securityPosture.completion_percentage >= 80 ? 'text-green-600' : 
                    securityPosture.completion_percentage >= 50 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {securityPosture.completion_percentage || 0}%
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {securityPosture.completed_count || 0} of {securityPosture.total_controls || 21} controls
                  </p>
                </div>
                <div className="bg-white border border-border rounded-sm p-4">
                  <p className="text-sm font-medium text-slate-600 mb-1">Completed</p>
                  <p className="text-3xl font-heading font-semibold text-green-600">
                    {securityPosture.completed_count || 0}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Implemented</p>
                </div>
                <div className="bg-white border border-border rounded-sm p-4">
                  <p className="text-sm font-medium text-slate-600 mb-1">To Address</p>
                  <p className="text-3xl font-heading font-semibold text-red-600">
                    {securityPosture.to_address_count || 0}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Action needed</p>
                </div>
                <div className="bg-white border border-border rounded-sm p-4">
                  <p className="text-sm font-medium text-slate-600 mb-1">Unknown</p>
                  <p className="text-3xl font-heading font-semibold text-slate-400">
                    {securityPosture.unknown_count || 0}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Unable to verify</p>
                </div>
              </div>

              {/* Security Recommendations List */}
              <div className="bg-white border border-border rounded-sm">
                <div className="p-4 border-b border-border">
                  <h3 className="text-lg font-semibold text-slate-900">Security Recommendations</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    {securityPosture.total_controls || 21} priority security controls checked via Microsoft Graph API
                  </p>
                </div>
                <div className="divide-y divide-border">
                  {securityPosture.recommendations?.map((rec, index) => (
                    <div key={rec.id || index} className="p-4 hover:bg-slate-50 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            {rec.status === 'Completed' && (
                              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                            )}
                            {rec.status === 'To Address' && (
                              <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                            )}
                            {rec.status === 'Unknown' && (
                              <AlertCircle className="w-5 h-5 text-slate-400 flex-shrink-0" />
                            )}
                            <h4 className="font-medium text-slate-900">{rec.title}</h4>
                          </div>
                          {rec.details && (
                            <p className="text-xs text-slate-500 mt-1 ml-7">
                              {rec.details}
                            </p>
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            rec.status === 'Completed' ? 'bg-green-100 text-green-800' :
                            rec.status === 'To Address' ? 'bg-red-100 text-red-800' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {rec.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">Azure AD App Configuration</h2>
            <p className="text-sm text-slate-600 mt-1">
              Configure your own Azure AD application to connect to Microsoft 365. 
              This allows you to control the permissions and maintain the app registration in your own tenant.
            </p>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Redirect URI Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
              <h3 className="font-medium text-blue-900 mb-2">Redirect URI (Required for Azure AD App)</h3>
              <p className="text-sm text-blue-700 mb-3">
                Add this URL to your Azure AD app&apos;s Redirect URIs in the Authentication section:
              </p>
              <div className="flex items-center gap-2 bg-white rounded border border-blue-200 p-2">
                <code className="flex-1 text-sm text-blue-800 break-all">{redirectUri}</code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(redirectUri)}
                  className="flex-shrink-0"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* Client ID */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Application (Client) ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={config.client_id}
                onChange={(e) => setConfig({ ...config, client_id: e.target.value })}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
              />
              <p className="text-xs text-slate-500 mt-1">
                Found in Azure Portal → App Registration → Overview
              </p>
            </div>

            {/* Client Secret */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Client Secret <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showSecret ? 'text' : 'password'}
                  value={config.client_secret}
                  onChange={(e) => setConfig({ ...config, client_secret: e.target.value })}
                  placeholder="Enter client secret value"
                  className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showSecret ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Found in Azure Portal → App Registration → Certificates & secrets
              </p>
            </div>

            {/* Azure Tenant ID */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Directory (Tenant) ID
              </label>
              <input
                type="text"
                value={config.azure_tenant_id}
                onChange={(e) => setConfig({ ...config, azure_tenant_id: e.target.value })}
                placeholder="common"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
              />
              <p className="text-xs text-slate-500 mt-1">
                Use &quot;common&quot; for multi-tenant apps, or your specific tenant ID for single-tenant
              </p>
            </div>

            {/* Required Permissions */}
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
              <h3 className="font-medium text-slate-900 mb-2">Required API Permissions (Application)</h3>
              <p className="text-sm text-slate-600 mb-3">
                Configure these Microsoft Graph Application permissions and grant admin consent:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs max-h-64 overflow-y-auto">
                {[
                  'AccessReview.Read.All',
                  'AuditLog.Read.All',
                  'Device.Read.All',
                  'DeviceManagementApps.Read.All',
                  'DeviceManagementConfiguration.Read.All',
                  'DeviceManagementManagedDevices.Read.All',
                  'Directory.Read.All',
                  'IdentityRiskEvent.Read.All',
                  'IdentityRiskyServicePrincipal.Read.All',
                  'IdentityRiskyUser.Read.All',
                  'InformationProtectionConfig.Read.All',
                  'InformationProtectionPolicy.Read.All',
                  'Policy.Read.All',
                  'Reports.Read.All',
                  'RoleManagement.Read.All',
                  'SecurityAlert.Read.All',
                  'SecurityEvents.Read.All',
                  'ThreatHunting.Read.All',
                  'ThreatIndicators.Read.All',
                  'ThreatSubmission.Read.All',
                  'User.Read.All',
                  'UserAuthenticationMethod.Read.All'
                ].map((perm) => (
                  <div key={perm} className="flex items-center gap-1 text-slate-600">
                    <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                    <span className="truncate">{perm}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded">
                <p className="text-xs text-amber-800">
                  <strong>Important:</strong> After adding permissions, click &quot;Grant admin consent&quot; in Azure Portal.
                </p>
              </div>
              <a
                href="https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 mt-3"
              >
                <ExternalLink className="w-4 h-4" />
                Open Azure Portal
              </a>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-slate-200">
              <Button
                onClick={handleSaveConfig}
                disabled={savingConfig || !config.client_id}
                className="bg-slate-900 text-white hover:bg-slate-800"
              >
                {savingConfig ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Configuration'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
